import { create } from 'zustand'
import { projectsApiService, ProjectApiResponse } from '../services/projectsApi'

interface ProjectsState {
  projects: ProjectApiResponse[]
  loading: boolean
  error: string | null
  selectedProjects: string[]
}

export interface ProjectData {
  name: string
  description: string
  tenantId: string
  status?: 'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'IN_REVIEW'
  owners?: string[]
}

export interface UpdateProjectData {
  name?: string
  description?: string
  status?: 'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'IN_REVIEW'
  owners?: string[]
}

interface ProjectsActions {
  fetchProjects: (tenantId?: string | null) => Promise<void>
  createProject: (projectData: ProjectData) => Promise<void>
  updateProject: (projectId: string, projectData: UpdateProjectData) => Promise<void>
  deleteProject: (projectId: string) => Promise<void>
  toggleProjectSelection: (projectId: string) => void
  clearSelectedProjects: () => void
  selectAllProjects: () => void
  setError: (error: string | null) => void
}

type ProjectsStore = ProjectsState & ProjectsActions

export const useProjectsStore = create<ProjectsStore>((set) => ({
  // State
  projects: [],
  loading: false,
  error: null,
  selectedProjects: [],

  // Actions
  fetchProjects: async (tenantId?: string | null) => {
    set({ loading: true, error: null })
    try {
      const response = await projectsApiService.getProjects(tenantId)
      if (response.success && response.data) {
        set({ projects: response.data, loading: false })
      } else {
        set({ error: response.message || 'Failed to fetch projects', loading: false })
      }
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to fetch projects', 
        loading: false 
      })
    }
  },

  createProject: async (projectData) => {
    set({ loading: true, error: null })
    try {
      const response = await projectsApiService.createProject(projectData)
      if (response.success && response.data) {
        set(state => ({ 
          projects: [...state.projects, response.data!], 
          loading: false 
        }))
      } else {
        set({ error: response.message || 'Failed to create project', loading: false })
      }
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to create project', 
        loading: false 
      })
    }
  },

  updateProject: async (projectId, projectData) => {
    set({ loading: true, error: null })
    try {
      const response = await projectsApiService.updateProject(projectId, projectData)
      if (response.success && response.data) {
        set(state => ({
          projects: state.projects.map(project => 
            project._id === projectId ? response.data! : project
          ),
          loading: false
        }))
      } else {
        set({ error: response.message || 'Failed to update project', loading: false })
      }
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to update project', 
        loading: false 
      })
    }
  },

  deleteProject: async (projectId) => {
    set({ loading: true, error: null })
    try {
      const response = await projectsApiService.deleteProject(projectId)
      if (response.success) {
        set(state => ({
          projects: state.projects.filter(project => project._id !== projectId),
          selectedProjects: state.selectedProjects.filter(id => id !== projectId),
          loading: false
        }))
      } else {
        set({ error: response.message || 'Failed to delete project', loading: false })
      }
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to delete project', 
        loading: false 
      })
    }
  },

  toggleProjectSelection: (projectId) => {
    set(state => ({
      selectedProjects: state.selectedProjects.includes(projectId)
        ? state.selectedProjects.filter(id => id !== projectId)
        : [...state.selectedProjects, projectId]
    }))
  },

  clearSelectedProjects: () => {
    set({ selectedProjects: [] })
  },

  selectAllProjects: () => {
    set(state => ({ selectedProjects: state.projects.map(project => project._id) }))
  },

  setError: (error) => {
    set({ error })
  }
}))
